import { AxiosInstance } from "axios";

import i18n from "multitenancy/i18n";

import { ToastMessage, useToast } from "hooks/toast";

import { CreateOitiva, CreateRecord } from "features/cruds/Drawer";
import { IOptionsFilterStatus } from "features/Modal/HearingFilter/interface";

import ModalDefault from "components/ModalDefault";

import { baseURL } from "services/api";
import { audiencesServices, IFilterParameters } from "services/audiences";
import {
  hearingFileService as hearingFileServiceLocal,
  hearingFileServiceV2,
  IHearingFileBody,
  IHearingFileDownloads,
} from "services/hearingFile";
import { IntegrationService } from "services/integrations";

import { LogOperation } from "utils/entities/logOperation";
import { isRecentAction } from "utils/lastAction";

import { IAudience } from "./interface";

const unwrap = (res: any) => (res?.data ?? res?.body ?? res) as any;

const getListDownloads = async (hearingId: number) => {
  if (hearingId !== undefined) {
    const list =
      await hearingFileServiceLocal.getAllDownloadsWithoutUrlByHearingId(
        hearingId,
      );

    if (list.status === 200) {
      const listAux = list.data.data;
      return listAux as Array<IHearingFileDownloads>;
    }
  }

  return [];
};

export type SearchScope = "general" | "current" | "county";

export interface FilterExtras {
  attendantId?: number | null;
  profile?: any;
  scope?: SearchScope;
}

function normalizeList(payload: any): any[] {
  if (Array.isArray(payload)) return payload;
  const candidates = [
    payload?.list,
    payload?.data?.list,
    payload?.data,
    payload?.items,
    payload?.Data,
    payload?.result,
    payload?.content,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
}

export const handleItemClicked = (
  element: IHearingFileDownloads,
  _setLoading: any,
  addToast: any,
) => {
  hearingFileServiceLocal
    .getDownloadToken(element.hearingFileId)
    .then((response) => {
      if (response.success && response.data) {
        const downloadUrl = `${baseURL}${response.data.downloadUrl}`;
        window.open(downloadUrl, "_blank");

        addToast({
          type: "info",
          title: i18n.t("downloads.started"),
          description: i18n.t("downloads.startedDescription"),
        });
      } else {
        addToast({
          type: "error",
          title: i18n.t("downloads.error"),
          description: response.message || i18n.t("downloads.errorDescription"),
        });
      }
    })
    .catch(() => {
      addToast({
        type: "error",
        title: i18n.t("downloads.error"),
        description: i18n.t("downloads.errorGeneric"),
      });
    });
};

export const handleOpenDrawerDownloads = async (
  hearingId: number,
  setOpenDownloads: React.Dispatch<React.SetStateAction<boolean>>,
  setListDownloads: React.Dispatch<
    React.SetStateAction<IHearingFileDownloads[]>
  >,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  exportPermission: boolean | undefined,
) => {
  setOpenDownloads(true);
  setLoading(true);
  try {
    if (exportPermission) {
      const response = await getListDownloads(hearingId);
      setListDownloads(response);
    } else {
      setListDownloads([]);
    }
    setLoading(false);
  } finally {
    setLoading(false);
  }
};

export const hearingDelete = async (
  hearingId: number,
  listRecord: any,
  setListRecord: React.Dispatch<any>,
) => {
  const { data, status } = await hearingFileServiceV2.deleteById(hearingId);
  await LogOperation.postLogOperation("HearingFile", 3, 3);
  if (status === 200) {
    const newHearingScheduled = listRecord.hearingScheduled?.filter(
      (x: any) => x?.id !== hearingId,
    );
    setListRecord({ ...listRecord, hearingScheduled: newHearingScheduled });
  }
  return { data, status };
};

interface IShowModalDeleteAlert {
  hearingId: number;
  setHearingFileIdToDelete: React.Dispatch<React.SetStateAction<number>>;
  OpenModalConfirmDelete: boolean;
  setOpenModalConfirmDelete: React.Dispatch<React.SetStateAction<boolean>>;
  listRecord: any;
  setListRecord: React.Dispatch<any>;
}

export const ShowModalDeleteAlert: React.FC<IShowModalDeleteAlert> = ({
  hearingId,
  setHearingFileIdToDelete,
  OpenModalConfirmDelete,
  setOpenModalConfirmDelete,
  listRecord,
  setListRecord,
}) => {
  const { addToast } = useToast();
  return (
    <ModalDefault
      openModal={OpenModalConfirmDelete}
      setOpenModal={setOpenModalConfirmDelete}
      title={i18n.t("audiences.delete")}
      bodyMessageTitle={i18n.t("audiences.deleteScheduled")}
      bodyMessageSubTitle={i18n.t("audiences.cannotUndo")}
      textConfirm={i18n.t("buttons.delete")}
      onClickConfirm={() =>
        handleResultDeleteHearingFile(
          true,
          hearingId,
          setHearingFileIdToDelete,
          setOpenModalConfirmDelete,
          listRecord,
          setListRecord,
          addToast,
        )
      }
      textCancel={i18n.t("buttons.cancel")}
      onClickCancel={() =>
        handleResultDeleteHearingFile(
          false,
          hearingId,
          setHearingFileIdToDelete,
          setOpenModalConfirmDelete,
          listRecord,
          setListRecord,
          addToast,
        )
      }
      bodyMessageMarginBetweenTitleAndSubTitle="30px"
      bodyMessageMarginTop="30px"
    />
  );
};

const handleResultDeleteHearingFile = async (
  confirmed: boolean,
  hearingId: number,
  setHearingFileIdToDelete: React.Dispatch<React.SetStateAction<number>>,
  setOpenModalConfirmDelete: React.Dispatch<React.SetStateAction<boolean>>,
  listRecord: any,
  setListRecord: React.Dispatch<any>,
  addToast: (messages: Omit<ToastMessage, "id">) => void,
) => {
  if (confirmed) {
    const { data, status } = await hearingDelete(
      hearingId,
      listRecord,
      setListRecord,
    );

    if (status !== 200) {
      addToast({
        type: "error",
        title: i18n.t("audiences.scheduledTitle"),
        description: data.message,
      });
    } else {
      addToast({
        type: "success",
        title: i18n.t("audiences.scheduledTitle"),
        description: data.message,
      });
    }
    setOpenModalConfirmDelete(false);
    return;
  }
  setOpenModalConfirmDelete(false);
  setHearingFileIdToDelete(0);
};

interface ICreateRecordEdit {
  api: AxiosInstance;
  openEditRecord: boolean;
  setOpenEditRecord: React.Dispatch<React.SetStateAction<boolean>>;
  hearingFileToEdit: any;
  date: string | null | undefined;
  setDate: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  time: string | undefined;
  setTime: React.Dispatch<React.SetStateAction<string | undefined>>;
  setRecordEditedReturn: React.Dispatch<
    React.SetStateAction<IHearingFileBody | undefined>
  >;
  isInqueritoProduct: boolean;
  customerId?: number;
}

export const CreateRecordEdit: React.FC<ICreateRecordEdit> = ({
  api,
  openEditRecord,
  setOpenEditRecord,
  hearingFileToEdit,
  date,
  setDate,
  time,
  setTime,
  setRecordEditedReturn,
  isInqueritoProduct,
  customerId,
}) => {
  const { addToast } = useToast();
  const RecordComponent = isInqueritoProduct ? CreateOitiva : CreateRecord;

  return (
    <RecordComponent
      title={i18n.t("recordings.singular")}
      api={api}
      isEdit={true}
      addToast={addToast}
      setClose={() => setOpenEditRecord(false)}
      isOpen={openEditRecord}
      recordToEdit={hearingFileToEdit}
      date={date as any}
      setDate={setDate}
      time={time}
      setTime={setTime}
      setRecordEditedReturn={setRecordEditedReturn}
      customerId={customerId}
    />
  );
};

export const updateSchaduledList = (
  recordEditedReturn: IHearingFileBody,
  listRecord: any,
  setListRecord: React.Dispatch<any>,
  addToast: (messages: Omit<ToastMessage, "id">) => void,
) => {
  const listScheduledAux = listRecord.hearingScheduled;

  const indexItem = listScheduledAux.findIndex(
    (item: any) => item?.id === recordEditedReturn?.id,
  );

  if (indexItem !== -1) {
    listScheduledAux[indexItem] = recordEditedReturn;
  } else {
    addToast({
      title: i18n.t("common.error"),
      description: i18n.t("recordings.updateError"),
      type: "error",
    });
    return;
  }
  setListRecord({ ...listRecord, hearingScheduled: listScheduledAux });
};

// export const filterSearch = async (
//   placeId: number | string,
//   districtId: number | string,
//   hearingNumber: string,
//   startDate: string | null | undefined,
//   finishDate: string | null | undefined,
//   _statusOption: any,
//   _valueOptionDropDown: IOptionsFilterStatus,
//   activeFilter: boolean | null,
//   setIsFilter: React.Dispatch<React.SetStateAction<boolean>>,
//   setOpenHearingFilter: React.Dispatch<React.SetStateAction<boolean>>,
//   setAudiences: React.Dispatch<React.SetStateAction<IAudience[]>>,
//   setAudience: React.Dispatch<any>,
//   setLoading: React.Dispatch<React.SetStateAction<boolean>>,
//   addToast: (messages: Omit<ToastMessage, "id">) => void,
//   setStartDateError: React.Dispatch<React.SetStateAction<boolean>>,
//   setFinishDateError: React.Dispatch<React.SetStateAction<boolean>>,
//   isCountyFilter: boolean,
//   hearingTypeIdFilter: number | null,
//   statusFilter: string | null,
//   _hearingTypeId?: number | null,
//   extras: FilterExtras = {},
// ) => {
//   const startDateAux: string | undefined =
//     !startDate || startDate === "Invalid Date" ? undefined : `${startDate}`;
//   const finishDateAux: string | undefined =
//     !finishDate || finishDate === "Invalid Date" ? undefined : `${finishDate}`;

//   if (startDateAux === undefined && finishDateAux !== undefined) {
//     addToast({
//       title: i18n.t("common.warning"),
//       description: i18n.t("audiences.filter.fillBothDates"),
//       type: "warning",
//     });
//     setStartDateError(true);
//     return;
//   } else if (startDateAux !== undefined && finishDateAux === undefined) {
//     addToast({
//       title: i18n.t("common.warning"),
//       description: i18n.t("audiences.filter.fillBothDates"),
//       type: "warning",
//     });
//     setFinishDateError(true);
//     return;
//   }

//   if (
//     startDateAux &&
//     finishDateAux &&
//     new Date(startDateAux) > new Date(finishDateAux)
//   ) {
//     addToast({
//       title: i18n.t("common.warning"),
//       description: i18n.t("audiences.filter.invalidPeriod"),
//       type: "warning",
//     });
//     setStartDateError(true);
//     setFinishDateError(true);
//     return;
//   }

//   setLoading(true);
//   let combinedList: IAudience[] = [];

//   const attendant = extras.attendantId || null;
//   const baseParams: IFilterParameters = {
//     placeId: placeId as number | string,
//     district: isCountyFilter,
//     procedureNumber: hearingNumber || undefined,
//     startDate: startDateAux,
//     finishDate: finishDateAux,
//     status: statusFilter ?? undefined,
//     active: activeFilter ?? undefined,
//     hearingTypeId: hearingTypeIdFilter ?? undefined,
//     attendantId: attendant ?? undefined,
//     globalSearch: Boolean(attendant),
//   };

//   if (attendant) {
//     const resp: any = await audiencesServices.getAllByFiltersV2(baseParams);
//     const combinedList = normalizeList(unwrap(resp)) as IAudience[];

//     setAudiences(combinedList);
//     setIsFilter(true);
//     setStartDateError(false);
//     setFinishDateError(false);
//     setOpenHearingFilter(false);
//     setLoading(false);
//     return;
//   }

//   if (isCountyFilter) {
//     const resp: any = await audiencesServices.getAllByFiltersV2(baseParams);
//     combinedList = normalizeList(unwrap(resp)) as IAudience[];
//   } else {
//     try {
//       const resp: any = await audiencesServices.getAllByFiltersV2(baseParams);
//       combinedList = normalizeList(unwrap(resp)) as IAudience[];
//     } catch {
//       combinedList = [];
//     }
//   }

//   setAudiences(combinedList);

//   const usingFilters =
//     (!!hearingNumber && hearingNumber.trim() !== "") ||
//     !!startDateAux ||
//     !!finishDateAux ||
//     statusFilter != null ||
//     hearingTypeIdFilter != null;

//   const hasCountyScope = isCountyFilter === true;
//   const hasParticipant = Boolean(extras?.attendantId);

//   setIsFilter(usingFilters || hasCountyScope || hasParticipant);
//   setStartDateError(false);
//   setFinishDateError(false);
//   setAudience(null);
//   setLoading(false);
//   setOpenHearingFilter(false);
// };

export const clearAudienceFilter = async (
  placeId: number,
  clearDatesFilter: number,
  setHearingNumber: React.Dispatch<React.SetStateAction<string>>,
  setStartDate: React.Dispatch<React.SetStateAction<string | null | undefined>>,
  setFinishDate: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >,
  setActiveFilter: React.Dispatch<React.SetStateAction<boolean>>,
  setValueOptionDropDown: React.Dispatch<any>,
  setClearDatesFilter: React.Dispatch<React.SetStateAction<number>>,
  setAudiences: React.Dispatch<React.SetStateAction<IAudience[]>>,
  setIsFilter: React.Dispatch<React.SetStateAction<boolean>>,
  setAudience: React.Dispatch<any>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setStartDateError: React.Dispatch<React.SetStateAction<boolean>>,
  setFinishDateError: React.Dispatch<React.SetStateAction<boolean>>,
  setIsCountyFilter: React.Dispatch<React.SetStateAction<boolean>>,
  setHearingTypeIdFilter: React.Dispatch<React.SetStateAction<number | null>>,
  setStatusFilter: React.Dispatch<React.SetStateAction<string | null>>,
  setAttendantFilterId: React.Dispatch<React.SetStateAction<number | null>>,
) => {
  setLoading(true);
  setHearingNumber("");
  setStartDate(null);
  setFinishDate(null);
  setValueOptionDropDown(undefined);
  setActiveFilter(true);
  setClearDatesFilter(clearDatesFilter + 1);
  setIsFilter(false);
  setStartDateError(false);
  setFinishDateError(false);
  setIsCountyFilter(false);
  setHearingTypeIdFilter(null);
  setStatusFilter(null);
  setAttendantFilterId(null);

  const params: IFilterParameters = {
    placeId: placeId,
    procedureNumber: undefined,
    startDate: undefined,
    finishDate: undefined,
    status: undefined,
    active: true,
  };

  const response = await audiencesServices.getAllByFiltersV2(params);
  const payload = unwrap(response);
  const list = normalizeList(payload) as IAudience[];
  setAudiences(list);
  setLoading(false);
};

export const getFirstRecord = (listRecord: any) => {
  if (listRecord) {
    return listRecord.hearingInProgress?.find(
      (item: any) => item.status === 13,
    );
  }
  return null;
};

export const checkInputsFilled = async (
  addToast: (messages: Omit<ToastMessage, "id">) => void,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  inputValues: { [key: string]: string },
) => {
  const allFilled = () => {
    for (const key in inputValues) {
      if (!inputValues[key]) {
        return false;
      }
    }
    return true;
  };
  if (allFilled()) {
    try {
      const [, firstValue] = Object.entries(inputValues)[0];

      const hearingIdIsNumber = Number(firstValue);
      if (isNaN(hearingIdIsNumber)) {
        addToast({
          type: "warning",
          title: i18n.t("notifications.title"),
          description: i18n.t("notifications.fillRequiredFields"),
        });
        return false;
      } else {
        setLoading(true);
        const hearingId = parseInt(firstValue);
        const response = await IntegrationService.notify(hearingId);

        if (response.status === 200) {
          addToast({
            type: "success",
            title: i18n.t("notifications.title"),
            description: response.data.message,
          });
          return true;
        } else {
          addToast({
            type: "error",
            title: i18n.t("notifications.title"),
            description: response.data.message,
          });
          return false;
        }
      }
    } finally {
      setLoading(false);
    }
  } else {
    addToast({
      type: "warning",
      title: i18n.t("notifications.title"),
      description: i18n.t("notifications.fillRequiredFields"),
    });
    return false;
  }
};

export const updateStatusHearing = (
  hearingIdToUpdate: any,
  hearingList: IAudience[],
  newStatus: string,
  setAudiences: React.Dispatch<React.SetStateAction<IAudience[]>>,
  listRecord: any,
  setListRecord: React.Dispatch<any>,
) => {
  const newList = hearingList.map((item) =>
    item.id === hearingIdToUpdate ? { ...item, status: newStatus } : item,
  );
  setAudiences(newList);
  const patch = (arr?: any[]) =>
    Array.isArray(arr)
      ? arr.map((x) =>
          x?.id === hearingIdToUpdate ? { ...x, status: newStatus } : x,
        )
      : arr;

  setListRecord({
    ...listRecord,
    hearingScheduled: patch(listRecord?.hearingScheduled),
    hearingInProgress: patch(listRecord?.hearingInProgress),
    hearingFinished: patch(listRecord?.hearingFinished),
  });
};

export const onMigrateDataToAnotherHearing = async (
  currentHearingId: number | string,
  hearingIdToMigrate: number | string,
  swapMode: 0 | 1 | 2,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  addToast: (messages: Omit<ToastMessage, "id">) => void,
  updateAudiences: () => Promise<void>,
  onClose: () => void,
) => {
  try {
    setLoading(true);
    const { data, success } =
      await audiencesServices.onMigrateDataToAnotherHearing(
        currentHearingId,
        hearingIdToMigrate,
        swapMode,
      );

    if (success) {
      addToast({
        type: "success",
        title: i18n.t("audiences.migrateData"),
        description: data.message,
      });
      await updateAudiences();
    } else {
      addToast({
        type: "error",
        title: i18n.t("audiences.migrateData"),
        description: data.message || i18n.t("audiences.migrateNotPossible"),
      });
    }

    onClose();
  } catch (error) {
    addToast({
      type: "error",
      title: i18n.t("audiences.migrateData"),
      description: i18n.t("audiences.migrateError"),
    });
  } finally {
    setLoading(false);
  }
};

export const updateStatusHearingSafely = (
  hearingIdToUpdate: number | string,
  hearingList: IAudience[],
  newStatus: string,
  setAudiences: React.Dispatch<React.SetStateAction<IAudience[]>>,
  listRecord: any,
  setListRecord: React.Dispatch<any>,
) => {
  const current = hearingList.find((h) => h.id === hearingIdToUpdate)?.status;
  if (
    isRecentAction("attachment:delete") &&
    newStatus === "G" &&
    current !== "G"
  ) {
    return;
  }
  updateStatusHearing(
    hearingIdToUpdate,
    hearingList,
    newStatus,
    setAudiences,
    listRecord,
    setListRecord,
  );
};

//#region Web Socket Hearings

export const handleChangeHearingsFromWebSocket = (
  webSocketHearingResponse: any,
  setAudiences: React.Dispatch<React.SetStateAction<IAudience[]>>,
  setTotalCount?: React.Dispatch<React.SetStateAction<number>>,
  expectedPlaceId?: number,
  isDistrictSearch = false,
) => {
  try {
    const jsonToFormat = JSON.parse(webSocketHearingResponse);
    let incoming = formatWebSocket(jsonToFormat);
    if (expectedPlaceId && !isDistrictSearch && incoming.length > 0) {
      const belongsToCurrentPlace = incoming.some(
        (item) => item.placeId === expectedPlaceId,
      );
      if (!belongsToCurrentPlace) return;
      incoming = incoming.filter((item) => item.placeId === expectedPlaceId);
    }
    const totalCount = jsonToFormat?.Data?.TotalCount;
    if (typeof totalCount === "number") {
      setTotalCount?.(totalCount);
    }

    setAudiences((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return incoming;

      const merged = incoming.map((item) => {
        const prevItem = prev.find((p) => p.id === item.id);
        if (!prevItem) return item;

        const patched = {
          ...item,
          customFields: prevItem.customFields,
        };

        if (
          isRecentAction("attachment:delete") &&
          item.status === "G" &&
          prevItem.status !== "G"
        ) {
          return { ...patched, status: prevItem.status };
        }
        return patched;
      });

      return merged;
    });
  } catch (error) {
    console.log("error", error);
  }
};

const formatWebSocket = (response: any) => {
  const responseItems = response?.Data?.Items ?? response?.Data;
  const adaptedData: IAudience[] = Array.isArray(responseItems)
    ? responseItems.map((item: any) => ({
        id: item.Id,
        guid: item.Guid,
        foreignKey: item.ForeignKey,
        placeId: item.PlaceId,
        hearingTypeId: item.HearingTypeId,
        hearingTypeName: item.HearingTypeName,
        status: item.Status,
        procedureNumber: item.ProcedureNumber,
        expectedDate: item.ExpectedDate,
        startDate: item.StartDate,
        finishDate: item.FinishDate,
        duration: item.Duration,
        description: item.Description,
        procedureCustomerSourceName: "",
        userId: item.UserId,
        exportCount: item.ExportCount,
        isClassified: item.IsClassified,
        confidentialityLevel: item.ConfidentialityLevel,
        download: item.Download,
        extraContent: item.ExtraContent,
        notificationDate: item.NotificationDate,
        notificationMessage: item.NotificationMessage,
        active: item.Active,
        device: item.Device,
        ip: item.IP,
        logPoliceUnitId: item.LogPoliceUnitId,
        logUserId: item.LogUserId,
        recordMode: item.RecordMode,
      }))
    : [];
  return adaptedData;
};

//#endregion

export const getRecordModeList = () => [
  {
    id: "1",
    name: i18n.t("recordings.recordMode.videoconference"),
    disabled: false,
  },
  {
    id: "2 ",
    name: i18n.t("recordings.recordMode.local"),
    disabled: import.meta.env.VITE_APP_ENABLE_LOCAL_RECORD_MODE !== "true",
  },
];
